import { useEffect, useState } from "react";
import type { RestaurantFloorState } from "@/services/floorBus";
import { useFloorSource } from "@/services/showcase";
import type { RestaurantId } from "@/types/restaurant";

/**
 * Live floor state for a single restaurant — derived from the shared
 * WebSocket bus (or, inside the `/showcase-cluj` demo, its local simulator).
 * Returns `null` for the brief window before the snapshot lands; callers
 * should fall back to whatever cold-start data they have.
 *
 * Pass `enabled = false` (e.g. for a venue with POS view off) to skip the
 * subscription entirely — the hook then always returns `null`.
 */
export function useFloorState(
  restaurantId: RestaurantId,
  enabled = true,
): RestaurantFloorState | null {
  const source = useFloorSource();
  const [slice, setSlice] = useState<RestaurantFloorState | null>(
    () => (enabled ? source.getState()[restaurantId] ?? null : null),
  );

  useEffect(() => {
    if (!enabled) {
      setSlice(null);
      return;
    }
    return source.subscribe((all) => {
      setSlice(all[restaurantId] ?? null);
    });
  }, [restaurantId, enabled, source]);

  return enabled ? slice : null;
}
